import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PayBadge } from "@/components/PaymentHistory";

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-border/70 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold" data-testid={testId}>{value}</div>
    </div>
  );
}

export default function PaymentsTab() {
  const [all, setAll] = useState(null);
  const [status, setStatus] = useState("all");
  const [mode, setMode] = useState(null);
  useEffect(() => {
    api.get("/admin/payments").then((r) => setAll(r.data));
    api.get("/payments/config").then((r) => setMode(r.data));
  }, []);
  if (!all) return <Skeleton className="h-64" />;
  const isPending = (p) => p.status === "created" || p.status === "pending";
  const rows = status === "all" ? all : all.filter((p) => (status === "pending" ? isPending(p) : p.status === status));
  const revenue = all.filter((r) => r.status === "paid").reduce((n, r) => n + r.amount, 0);
  return (
    <Card className="border-border/70" data-testid="payments-tab">
      <CardContent className="space-y-5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-lg font-bold">Transactions</h3>
          {mode?.mock ? (
            <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15" data-testid="payments-mock-badge">MOCK MODE — configure Razorpay keys in “Payment & Email” tab</Badge>
          ) : mode && (
            <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15" data-testid="payments-live-badge">{mode.key_id?.startsWith("rzp_live_") ? "LIVE" : "TEST"} · {mode.key_id}</Badge>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Stat label="Revenue" value={`₹${revenue.toLocaleString("en-IN")}`} testId="payments-revenue" />
          <Stat label="Successful" value={all.filter((p) => p.status === "paid").length} testId="payments-count-paid" />
          <Stat label="Pending" value={all.filter(isPending).length} testId="payments-count-pending" />
          <Stat label="Failed" value={all.filter((p) => p.status === "failed").length} testId="payments-count-failed" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All transactions</SelectItem>
            <SelectItem value="paid">Successful</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Student</TableHead><TableHead>Course</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Order / Payment ID</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell className="text-xs">{new Date(p.paid_at || p.created_at).toLocaleString("en-IN")}</TableCell>
                  <TableCell><div className="font-medium">{p.user?.name || "—"}</div><div className="text-xs text-muted-foreground">{p.user?.email}</div></TableCell>
                  <TableCell className="text-sm">{p.course_name}</TableCell>
                  <TableCell className="text-right font-mono text-xs">₹{p.amount.toLocaleString("en-IN")}</TableCell>
                  <TableCell>
                    <PayBadge status={p.status} />
                    {p.mock && <span className="ml-1 text-[10px] uppercase text-muted-foreground">mock</span>}
                    {p.failure_reason && p.status === "failed" && <div className="mt-1 max-w-[180px] text-[10px] text-rose-600">{p.failure_reason}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">{p.order_id}<br />{p.payment_id}</TableCell>
                  <TableCell>{p.status === "paid" && <Link to={`/receipt/${p.id}`} className="text-xs font-semibold text-[hsl(var(--brand-navy))] underline" data-testid={`admin-receipt-${p.id}`}>Receipt</Link>}</TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={7} className="py-8 text-center text-xs text-muted-foreground">No transactions.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
